import { Injectable, signal, inject } from "@angular/core";
import { Observable, of } from "rxjs";
import { map, catchError } from "rxjs/operators";

import { MeetDayService } from "src/app/api/services/meet-day.service";
import { ModalService } from "src/app/shared/services/modal.service";
import { ModelCompanyprofile } from "src/app/model/model.companyprofile";

/**
 * Service for managing companies in the Meetday modal.
 * This service handles company-specific operations and coordinates with
 * the facade for cross-tab state updates via callbacks.
 */
@Injectable()
export class MeetdayTabCompaniesService {
  private meetDayService = inject(MeetDayService);
  private modal = inject(ModalService);

  // ========== State ==========
  selectedCompany = signal<ModelCompanyprofile | null>(null);

  // ========== Company Selection ==========

  setSelectedCompany(company: ModelCompanyprofile | null): void {
    this.selectedCompany.set(company);
  }

  // ========== Company Registration ==========

  /**
   * Register a company for the meetday.
   * @param meetDayId The meetday ID
   * @returns Observable<boolean> indicating success
   */
  addCompanyToMeetday(meetDayId: string): Observable<boolean> {
    const company = this.selectedCompany();

    if (!meetDayId || !company) {
      return of(false);
    }

    return this.meetDayService
      .registerCompanyForMeetDay({
        id: meetDayId,
        body: { companyId: company.id },
      })
      .pipe(
        map(() => {
          this.selectedCompany.set(null);
          return true;
        }),
        catchError((error) => {
          console.error("Failed to register company:", error);
          this.modal.Alert(
            "Error",
            "Failed to register company for this event. Please try again."
          );
          return of(false);
        })
      );
  }

  /**
   * Unregister a company from the meetday.
   * @param meetDayId The meetday ID
   * @param companyId The company ID to unregister
   * @returns Observable<boolean> indicating success
   */
  deleteCompanyFromMeetday(
    meetDayId: string,
    companyId: string
  ): Observable<boolean> {
    if (!meetDayId) {
      return of(false);
    }

    return this.meetDayService
      .unregisterCompanyForMeetDay({
        id: meetDayId,
        body: { companyId },
      })
      .pipe(
        map(() => true),
        catchError((error) => {
          console.error("Failed to unregister company:", error);
          this.modal.Alert(
            "Error",
            "Failed to remove company from this event. Please try again."
          );
          return of(false);
        })
      );
  }

  // ========== Company Limit Management ==========

  /**
   * Update a company's student limit via API.
   * @param meetDayId The meetday ID
   * @param companyId The company ID
   * @param limit The new limit (or null for unlimited)
   */
  updateCompanyLimitViaAPI(
    meetDayId: string,
    companyId: string,
    limit: number | null
  ): void {
    if (!meetDayId || !companyId || (limit !== null && limit < 1)) {
      return;
    }

    this.meetDayService
      .updateStudentCapForCompany({
        id: meetDayId,
        body: {
          companyId,
          maxAmountOfStudentsForCompany: limit,
        },
      })
      .pipe(
        catchError((error) => {
          console.error(
            `Failed to update limit for company ${companyId}:`,
            error
          );
          this.modal.Alert(
            "Update Failed",
            "Failed to update company limit. Please try again."
          );
          return of(null);
        })
      )
      .subscribe();
  }

}
